import styles from "./testPlanHeaderTestCaseItem.module.scss";
import { MethodType } from "../../../../shared/components/methodType/methodType";
import infoIcon from "../../../../shared/images/info.svg";
import { StatusImage } from "../../../../shared/components/statusImage/statusImage";
import loader from "../../../../shared/images/loader-execute.gif";
import { TestCaseResult } from "../../../../shared/components/testCaseResult/testCaseResult";
import { TestrailResult } from "../../../../shared/components/testrailResult/testrailResult";
import {
  TestCaseTemplate,
  TransformTestCase
} from "../../../../types/testCaseTemplateTypes";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../../../store";
import { setSelectedTestCase } from "../../../../store/slices/testCaseTemplateSlice";
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { findTestPlanExecuteResponse } from "../../../../store/selectors";
import { useTranslation } from "react-i18next";
import editIcon from "../../../../shared/images/edit.svg";
import { Popup } from "../../../../shared/components/popup/popup";
import { useGetAllTestCaseTemplateQuery } from "../../../../api/testCaseTemplateService";
import { TestCaseTemplateModal } from "../../../../components/testCaseTemplateModal/testCaseTemplateModal";
import { TestCaseModal } from "../../../../components/testCaseModal/testCaseModal";
import { useGetAllScenariosQuery } from "../../../../api/scenarioService";
import {
  setEditTestCaseId,
  updateTestPlanExecuteResponse,
  updateTestPlanSelectedScenarios
} from "../../../../store/slices/testPlanSlice";
import { RunTestCasesButton } from "../../../../components/runTestCasesButton/runTestCasesButton";
import { getTransformTestCase } from "../../../../utils/testCaseUtils";
import { TestPlanScenario } from "../../../../types/testPlanTypes";
import { TransformExecuteResponse } from "../../../../types/clientTypes";

type TestPlanHeaderTestCaseItemProps = {
  testCase: TransformTestCase
  testCaseTemplates?: Array<TestCaseTemplate>;
  onClickRerun: () => void;
}

export const TestPlanHeaderTestCaseItem = memo(({ testCase, testCaseTemplates, onClickRerun }: TestPlanHeaderTestCaseItemProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const selectedScenarios = useSelector((state: RootState) => state.testPlanSlice.selectedScenarios);

  const statusCodes = useSelector((state: RootState) => state.mainSlice.statusCodes);
  const testCasesIsLaunched = useSelector((state: RootState) => state.mainSlice.testCasesIsLaunched);
  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const selectedTestCase = useSelector((state: RootState) => state.testCaseTemplateSlice.selectedTestCase);
  const executeResponse = useSelector((state: RootState) => findTestPlanExecuteResponse(state, testCase.internalId || ""));

  const editContainer = useRef<HTMLDivElement>(null);

  const [testCaseTemplate, setTestCaseTemplate] = useState<TestCaseTemplate>({
    name: "",
    responseParameters: [],
    methodId: 0,
    isPositive: false,
    testCaseTemplateFolderId: 0,
    requestParameters: [],
    statusCodeId: 0,
    requestHeaders: [],
    childTestCases: [],
    maximumExecutionTimeout: 0,
    environmentServers: [],
    priorityId: 0,
    isJsonSchemaValidation: false,
  });

  const [showEditPopup, setShowEditPopup] = useState(false);
  const [showEditTestCaseModal, setShowEditTestCaseModal] = useState(false);
  const [showEditTemplateModal, setShowEditTemplateModal] = useState(false);

  const { refetch: refetchTemplates } = useGetAllTestCaseTemplateQuery("");
  const { refetch: refetchScenarios } = useGetAllScenariosQuery("");

  const onClickEdit = useCallback(() => {
    setShowEditPopup(true);
  }, [setShowEditPopup]);

  const closeEditPopup = useCallback((): void => {
    setShowEditPopup(false);
  }, [setShowEditPopup]);

  const editTestCase = useCallback(() => {
    setShowEditTestCaseModal(true);
    closeEditPopup();
  }, [closeEditPopup, setShowEditTestCaseModal]);

  const editTemplate = useCallback(() => {
    setShowEditTemplateModal(true);
    closeEditPopup();
  }, [closeEditPopup, setShowEditTemplateModal]);

  const closeTestCaseTemplateModal = useCallback(() => {
    setShowEditTemplateModal(false);
  }, [setShowEditTemplateModal]);

  const updateTestCaseTemplate = useCallback(() => {
    dispatch(setEditTestCaseId(testCase.id));
    refetchTemplates();
    closeTestCaseTemplateModal();
  }, [testCase, closeTestCaseTemplateModal, refetchTemplates]);

  const deleteTestCaseTemplate = useCallback(() => {
    refetchTemplates();
    refetchScenarios();
    closeTestCaseTemplateModal();
  }, [closeTestCaseTemplateModal, refetchScenarios, refetchTemplates]);

  const onCloseTestCaseModal = useCallback((isSave: boolean) => {
    if (isSave) {
      dispatch(setEditTestCaseId(testCase.id));
      refetchTemplates();
      refetchScenarios();
    }

    setShowEditTestCaseModal(false);
  }, [testCase, refetchTemplates, refetchScenarios, setShowEditTestCaseModal]);

  const onClickRerunLocal = useCallback((): void => {
    const newSelectedScenarios: Array<TestPlanScenario> = [];

    let needUpdate = false;
    selectedScenarios.forEach(selectedScenario => {
      if (!needUpdate) {
        const newSelectedTestCases: Array<TransformTestCase> = [];
        selectedScenario.testCases.forEach(selectedTestCase => {
          if (selectedTestCase.internalId === testCase.internalId) {
            const newTransformTestCase = getTransformTestCase(selectedVariables, testCaseTemplate, selectedTestCase);
            newSelectedTestCases.push(newTransformTestCase);
            needUpdate = true;
          } else {
            newSelectedTestCases.push(selectedTestCase);
          }
        });
        newSelectedScenarios.push({
          ...selectedScenario,
          testCases: newSelectedTestCases
        })
      } else {
        newSelectedScenarios.push(selectedScenario);
      }
    });

    if (needUpdate) {
      onClickRerun();
      dispatch(updateTestPlanSelectedScenarios(newSelectedScenarios))
    }
  }, [selectedScenarios, testCase, selectedVariables, testCaseTemplate, onClickRerun, dispatch]);

  const updateExecuteResponse = useCallback((executeResponse: TransformExecuteResponse) => {
    debugger;
    dispatch(updateTestPlanExecuteResponse(executeResponse));
  }, [dispatch]);

  useEffect(() => {
    const newTemplate = testCaseTemplates?.find(item => item.id === testCase.testCaseTemplateId);
    if (newTemplate) {
      setTestCaseTemplate(newTemplate);
    }
  }, [setTestCaseTemplate, testCaseTemplates, testCase.testCaseTemplateId]);

  const onClickItem = useCallback((): void => {
    dispatch(setSelectedTestCase({ testCase, template: testCaseTemplate } ));
  }, [dispatch, testCase, testCaseTemplate]);

  const containerStyle = useMemo((): string => {
    let result = styles.container;
    if (testCase.internalId === selectedTestCase.internalId) {
      result += ` ${styles.selected}`;
    }
    return result;
  }, [selectedTestCase, testCase])

  const statusCodeName = useMemo(() => {
    return statusCodes.find(item => item.code === executeResponse.statusCode)?.name
  }, [executeResponse, statusCodes]);

  return (
    <>
      <div className={containerStyle}
           onClick={onClickItem}>
        <div className={styles.info}>
          <div className={testCaseTemplate.isPositive ? styles.positive : styles.negative}
               title={testCaseTemplate.isPositive ? t("testCaseTemplate.positive") : t("testCaseTemplate.negative")}></div>
          <div>
            <MethodType methodId={testCaseTemplate.methodId}/>
          </div>
          <span title={testCase.name}>{testCase.name}</span>
          {!!testCaseTemplate.description && (<img src={infoIcon} title={testCaseTemplate.description} alt=""/>)}
          {!testCasesIsLaunched && (<div className={styles.edit}
                                         style={{display: showEditPopup ? "flex" : "none"}}
                                         ref={editContainer}>
              <img src={editIcon} alt="" onClick={onClickEdit}/>
              <Popup isShow={showEditPopup}
                     classContainer={styles.popup}
                     left={-70}
                     top={5}
                     parent={editContainer.current}
                     onClose={closeEditPopup}
                     child={
                       <>
                         <div className={styles.item}
                              onClick={editTestCase}>
                           {t("testCase.edit")}
                         </div>
                         <div className={styles.item}
                              onClick={editTemplate}>
                           {t("testCaseTemplate.editTemplate")}
                         </div>
                       </>
                     }
              />
            </div>
          )}
        </div>
        <div className={styles.rerunBlock}>
          <div className={styles.rerun}>
            <RunTestCasesButton selectedTestCases={[testCase]}
                                clickRun={onClickRerunLocal}
                                updateExecuteResponse={updateExecuteResponse}
                                hideStopButton
                                testCaseTemplates={[testCaseTemplate]}/>
          </div>
        </div>
        <div className={styles.status}>
          {executeResponse.statusCode > 0 && (
            <>
              <StatusImage statusCodeId={executeResponse.statusCode}
                           statusCode={executeResponse.statusCode}/>
              {executeResponse.statusCode} {statusCodeName}
            </>
          )}
          {!!executeResponse.isLoad && (
            <img src={loader} alt=""/>
          )}
        </div>
        <TestCaseResult executeResponse={executeResponse}/>
        <div className={styles.timeContainer}>
          {!!executeResponse.time && (
              <div className={styles.time}>
                {executeResponse.time} {t("common.ms")}
              </div>
          )}
        </div><TestrailResult executeResponse={executeResponse}/>
      </div>

      {showEditTemplateModal && (
        <TestCaseTemplateModal onClose={closeTestCaseTemplateModal}
                               onCreate={closeTestCaseTemplateModal}
                               onDelete={deleteTestCaseTemplate}
                               onUpdate={updateTestCaseTemplate}
                               testCaseTemplate={testCaseTemplate}/>
      )}

      {showEditTestCaseModal && (
        <TestCaseModal onClose={onCloseTestCaseModal}
                       testCase={testCase}
                       testCaseTemplate={testCaseTemplate}/>
      ) }
    </>
  );
})
